import type { JssStyle } from 'jss';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { ChatBotWidgetData } from '@web/widgets/chat-bot/type/chat-bot-widget.type';

import { EButtonStyleType, EButtonType, EHorizontalPosition } from '@common/enums';
import { getBaseButtonData, getBaseButtonIconStyles } from '@web/widgets/common/base/base-button/base-button.styles';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

function getButtonStyles(
    {
        buttonBase,
        buttonHover,
        styleType,
        advancedRadius,
        advancedBorderSettings,
        isShadow,
        shadowSettings,
    }: ChatBotWidgetData,
    fontSize: number
): JssStyle {
    if (styleType === EButtonStyleType.custom) {
        return {
            ...getBaseButtonData({
                buttonBase,
                buttonHover,
                advancedRadius,
                advancedBorderSettings,
                isShadow,
                shadowSettings,
            }),
            ...getBaseButtonIconStyles(fontSize),
        };
    }

    const shadowStyle: JssStyle = {};

    if (isShadow) {
        const s = shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
        shadowStyle.boxShadow = `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`;
    }

    return {
        ...getBaseButtonIconStyles(fontSize),
        ...shadowStyle,
    };
}

function getChatBotIconStyles(height: number): JssStyle {
    return {
        marginRight: '10px',
        '&:last-child': {
            marginRight: 0,
        },
        '& img': {
            height: `${height}px`,
            marginRight: '10px',
            '&:last-child': {
                marginRight: 0,
            },
        },
    };
}

export function getStyles(
    { data, styles }: WebLegacyWidgetEntity<ChatBotWidgetData>,
    { isConstructor }: any
): JssStyle {
    const { type } = data;
    const horizontalPositionMatrix = {
        [EHorizontalPosition.L]: 'flex-start',
        [EHorizontalPosition.M]: 'center',
        [EHorizontalPosition.R]: 'flex-end',
    };

    const mobileMediaQueryKey = '@media (max-width: 480px)';
    const tabletMediaQueryKey = '@media (min-width: 481px) and (max-width: 992px)';
    let chatBotWrapper: any = {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: `${horizontalPositionMatrix[data.horizontalAlign]}`,
        margin: data.type === EButtonType.button ? '-10px' : '0',
        alignItems: 'center',
        transform: `rotate(${data?.position?.rotate ?? 0}deg)`,
        zIndex: data?.position?.zIndex,
        '& .sp-ui-button': {
            alignItems: 'center',
        },
        [mobileMediaQueryKey]: {
            transform: `rotate(${data?.position?.mobileRotate ?? 0}deg)`,
            zIndex: data?.isSynchronizeStyle ? data?.position?.zIndex : data?.position?.mobileZIndex,
        },
        '&.mobileWidgetViewWrap': {},
        '&.tabletWidgetViewWrap': {},
    };

    if (data.mobileHorizontalAlign) {
        chatBotWrapper = {
            ...chatBotWrapper,
            [mobileMediaQueryKey]: {
                ...chatBotWrapper[mobileMediaQueryKey],
                justifyContent: `${horizontalPositionMatrix[data.mobileHorizontalAlign]}`,
            },
            '&.mobileWidgetViewWrap': {
                justifyContent: `${horizontalPositionMatrix[data.mobileHorizontalAlign]}`,
            },
        };
    }

    if (data.tabletHorizontalAlign) {
        chatBotWrapper = {
            ...chatBotWrapper,
            [tabletMediaQueryKey]: {
                justifyContent: `${horizontalPositionMatrix[data.tabletHorizontalAlign]}`,
            },
            '&.tabletWidgetViewWrap': {
                justifyContent: `${horizontalPositionMatrix[data.tabletHorizontalAlign]}`,
            },
        };
    }

    if (!isConstructor) {
        if (data?.hideOnMobile) {
            chatBotWrapper[mobileMediaQueryKey] = { display: 'none' };
        }

        if (data?.hideOnTablet) {
            chatBotWrapper[tabletMediaQueryKey] = { display: 'none' };
        }

        if (data?.hideOnDesktop) {
            chatBotWrapper['@media (min-width: 993px)'] = { display: 'none' };
        }
    }

    if (isConstructor) {
        chatBotWrapper['&.mobileWidgetViewWrap'].transform = `rotate(${data?.position?.mobileRotate ?? 0}deg)`;
        chatBotWrapper['&.tabletWidgetViewWrap'].transform = `rotate(${data?.position?.mobileRotate ?? 0}deg)`;
    }

    if (type === EButtonType.icon) {
        return {
            chatBotWrapper,
            chatBotGroup: getChatBotIconStyles(data.height),
        };
    }

    return {
        chatBotWrapper,
        chatBotGroup: getButtonStyles(data, styles.fontSize as number),
    };
}
